import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { HardHat, Building2, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { EunerdMark } from "@/components/eunerd-mark";
import { cn } from "@/lib/utils";
import { completeOnboarding } from "@/lib/api-cadastros";
import { fetchCurrentUser } from "@/lib/auth";
import { useAuth } from "@/stores/auth";

type Choice = "nerd" | "client" | null;

export function SetupPage() {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const [choice, setChoice] = useState<Choice>(null);
  const [submitting, setSubmitting] = useState(false);

  // Sempre re-buscar /me ao montar — pega flag onboarding_completed atualizada do servidor.
  useEffect(() => {
    void fetchCurrentUser();
  }, []);

  if (!user) return null;

  // Staff Eunerd nunca passa pelo setup — vai direto pro dashboard.
  if (user.role === "super_admin" || user.role === "admin") {
    navigate("/", { replace: true });
    return null;
  }

  if (user.onboarding_completed) {
    navigate("/", { replace: true });
    return null;
  }

  const handleSubmit = async () => {
    if (!choice) return;
    setSubmitting(true);
    try {
      const updated = await completeOnboarding(choice, {
        name: user.name ?? undefined,
      });
      setUser(updated);
      toast.success("Perfil configurado!", {
        description: choice === "nerd" ? "Bem-vindo, técnico." : "Bem-vindo, cliente.",
      });
      navigate("/", { replace: true });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Erro ao salvar";
      toast.error("Falha no setup", { description: msg });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="relative min-h-dvh flex items-center justify-center px-6 overflow-hidden bg-background py-12">
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div className="absolute -top-32 -left-32 h-[480px] w-[480px] rounded-full bg-primary-300/20 blur-3xl" />
        <div className="absolute -bottom-32 -right-32 h-[520px] w-[520px] rounded-full bg-primary-200/30 blur-3xl" />
      </div>

      <div className="relative max-w-2xl w-full space-y-8 animate-fade-in-up">
        <div className="text-center space-y-4">
          <div className="flex justify-center">
            <EunerdMark className="h-12 w-auto" />
          </div>
          <h1 className="font-sans text-3xl sm:text-4xl font-semibold tracking-tight">
            Olá, {user.name?.split(" ")[0] ?? "tudo bem?"}
          </h1>
          <p className="text-muted-foreground text-base sm:text-lg max-w-md mx-auto">
            Pra começar, conta pra gente qual é o seu perfil:
          </p>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <ChoiceCard
            icon={HardHat}
            title="Sou técnico"
            subtitle="Quero atender chamados"
            description="Receba ofertas de serviço por WhatsApp, gerencie seus atendimentos e fature pelo portal."
            active={choice === "nerd"}
            onClick={() => setChoice("nerd")}
          />
          <ChoiceCard
            icon={Building2}
            title="Sou empresa"
            subtitle="Preciso de suporte"
            description="Abra chamados, acompanhe SLA, gerencie contratos e técnicos atendendo sua operação."
            active={choice === "client"}
            onClick={() => setChoice("client")}
          />
        </div>

        <div className="flex justify-center">
          <Button
            size="lg"
            disabled={!choice || submitting}
            onClick={handleSubmit}
            className="min-w-[200px]"
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Salvando...
              </>
            ) : (
              "Continuar"
            )}
          </Button>
        </div>
      </div>
    </main>
  );
}

function ChoiceCard({
  icon: Icon,
  title,
  subtitle,
  description,
  active,
  onClick,
}: {
  icon: typeof HardHat;
  title: string;
  subtitle: string;
  description: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "group relative text-left rounded-2xl border-2 p-6 transition-all duration-300 ease-spring",
        "bg-white/70 dark:bg-card/60 backdrop-blur-xl",
        "shadow-[0_1px_2px_rgba(15,23,42,0.04),0_8px_24px_-12px_rgba(15,23,42,0.10)]",
        "hover:-translate-y-1 hover:shadow-[0_4px_8px_rgba(15,23,42,0.04),0_24px_48px_-16px_rgba(15,23,42,0.18)]",
        active
          ? "border-primary-500 ring-4 ring-primary-500/20"
          : "border-slate-200/70 dark:border-white/10 hover:border-primary-300",
      )}
    >
      {active && (
        <CheckCircle2
          className="absolute top-4 right-4 h-5 w-5 text-primary-600"
          aria-hidden
        />
      )}
      <div
        className={cn(
          "inline-flex h-12 w-12 items-center justify-center rounded-xl mb-4 transition-colors",
          active
            ? "bg-primary-600 text-white"
            : "bg-primary-50 text-primary-700 group-hover:bg-primary-100",
        )}
      >
        <Icon className="h-6 w-6" aria-hidden />
      </div>
      <h3 className="font-semibold text-lg">{title}</h3>
      <p className="text-sm text-muted-foreground mb-2">{subtitle}</p>
      <p className="text-sm text-foreground/80 leading-relaxed">{description}</p>
    </button>
  );
}
